import React from 'react';
import { gql } from '@apollo/client';
import {
	LimitViewModel
} from '@/graphql/generated';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';

export interface LimitAutocompleteProps {
	value: string;
	onChange: (value: string, limit: LimitViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	enableCreateNew?: boolean;
	initialSelectedLabel?: string; // Display label to show when form loads with a selected value
}

// GraphQL query for limits
const LIMITS_QUERY = gql`
	query LimitsList(
		$where: LimitViewModelFilterInput
		$first: Int
		$after: String
	) {
		limits(where: $where, first: $first, after: $after) {
			edges {
				node {
					limitId
					description
					maxAmount
					minAmount
					maxStayMinutes
					minStayMinutes
					facility {
						name
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

// Helper functions for limit display
export const getLimitDisplayName = (limit: LimitViewModel): string => {
	return limit.name || 'Unnamed Limit';
};

export const getLimitSecondaryText = (limit: LimitViewModel): string => {
	const parts: string[] = [];

	// Show facility name
	if (limit.facility?.name) {
		parts.push(`Facility: ${limit.facility.name}`);
	}

	// Show amount limits
	if (limit.minAmount !== null && limit.maxAmount !== null) {
		parts.push(`Amount: ${limit.minAmount} - ${limit.maxAmount}`);
	} else if (limit.minAmount !== null) {
		parts.push(`Min Amount: ${limit.minAmount}`);
	} else if (limit.maxAmount !== null) {
		parts.push(`Max Amount: ${limit.maxAmount}`);
	}

	// Show time limits
	if (limit.minStayMinutes !== null && limit.maxStayMinutes !== null) {
		parts.push(`Stay: ${limit.minStayMinutes} - ${limit.maxStayMinutes} min`);
	} else if (limit.minStayMinutes !== null) {
		parts.push(`Min Stay: ${limit.minStayMinutes} min`);
	} else if (limit.maxStayMinutes !== null) {
		parts.push(`Max Stay: ${limit.maxStayMinutes} min`);
	}

	return parts.join(' | ');
};

// Autocomplete configuration for limits
const createLimitAutocompleteConfig = (enableCreateNew = false): AutocompleteConfig<LimitViewModel> => ({
	query: LIMITS_QUERY,
	queryName: 'limits',
	searchFields: ['description'],
	getDisplayName: getLimitDisplayName,
	getSecondaryText: getLimitSecondaryText,
	getId: (limit) => limit.limitId,
	pageSize: DEFAULT_PAGE_SIZE,
	// Enable details functionality
	enableDetails: true,
	entityType: 'limits',
	...(enableCreateNew && {
		createNew: {
			enabled: true,
			entityName: 'Limit',
			createFormComponent: React.lazy(() =>
				import('./limit-create-form').then(module => ({
					default: module.LimitCreateForm
				}))
			)
		}
	})
});

export const LimitAutocomplete: React.FC<LimitAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search limits...',
	disabled = false,
	className = '',
	enableCreateNew = false,
	initialSelectedLabel
}) => {
	const config = createLimitAutocompleteConfig(enableCreateNew);

	return (
		<GenericAutocomplete<LimitViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
		/>
	);
};
